<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'RentalProperty',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    rentalId: {
      type: String,
      required: true,
    },
    resourceId: {
      type: String,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const item = ref(null);
  const formId = ref(null);
  const formName = ref('rentalProperyt');
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    item: item.value,
    formId: formId.value,
    formName: formName.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_item = () => {
    return props.resourceId
      ? $fn.value.fetch('read_rental_property', {
          parameters: { id: props.resourceId, rental_id: props.rentalId },
        })
      : $fn.value.schemaDefaults('CreateRentalProperty', {
          valid_from: $fn.value.today(),
        });
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { item: $$load_item, formId: $$load_formId },
    ux: {},
    dataRefs: {
      item,
      formId,
      formName,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: props.resourceId
        ? $fn.value.gettext('Edit Rental Property')
        : $fn.value.gettext('Add Rental Property'),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.resourceId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('resourceId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4m0ovofqv_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_ojfadam2t_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_request_y0nozs3ga_getActionArgs = async (
    $event
  ) => {
    return {
      operation: item.value.id
        ? 'update_rental_property'
        : 'create_rental_property',
      data: { ...item.value, rental_id: props.rentalId },
    };
  };
  const act_2140198529698299_closeModal_a437j01ry_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        value: $event.data.response.data,
      },
    };
  };
  const act_2140198529698299_request_cncmh2y1o_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_rental_property',
      data: {
        id: item.value.id,
        rental_id: props.rentalId,
        locked: true,
        etag: item.value.etag,
      },
    };
  };
  const act_2167826738365259_closeModal_izi8n7g8g_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_4m0ovofqv =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4m0ovofqv_getActionArgs,
    });
  const inst_evh_7315092382398562_close_ttnm5sm9n =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4m0ovofqv,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_ttnm5sm9n],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_1ojw49j0t =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_4t76jdwtv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_1ojw49j0t,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_4t76jdwtv],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_00rnd8c8a =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_74esmhhtq =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_00rnd8c8a,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_74esmhhtq],
    event: 'close',
  });
  const act_1040167445267876_closeModal_ojfadam2t =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_ojfadam2t_getActionArgs,
    });
  const inst_evh_1040167445267876_success_et9rwkx27 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_ojfadam2t,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_request_y0nozs3ga = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_y0nozs3ga_getActionArgs,
    displayName: 'submit',
    events: [inst_evh_1040167445267876_success_et9rwkx27],
  });
  const inst_evh_1040167445267876_submit_kb7iyffp6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_y0nozs3ga,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_kb7iyffp6],
    event: 'submit',
  });
  const act_2140198529698299_closeModal_a437j01ry =
    new actionTypes.CloseModalAction({
      actionArgs: act_2140198529698299_closeModal_a437j01ry_getActionArgs,
    });
  const inst_evh_2140198529698299_success_q73fn1pn3 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeModal_a437j01ry,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_2140198529698299_request_cncmh2y1o = new actionTypes.RequestAction({
    actionArgs: act_2140198529698299_request_cncmh2y1o_getActionArgs,
    displayName: 'lock',
    events: [inst_evh_2140198529698299_success_q73fn1pn3],
  });
  const inst_evh_2140198529698299_clickPreventStop_ld433incl =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_request_cncmh2y1o,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_ld433incl],
      event: 'click.prevent.stop',
    });
  const act_2167826738365259_closeModal_izi8n7g8g =
    new actionTypes.CloseModalAction({
      actionArgs: act_2167826738365259_closeModal_izi8n7g8g_getActionArgs,
    });
  const inst_evh_2167826738365259_clickPreventStop_gsbw9x5ki =
    new actionTypes.EventHandlerImpl({
      action: act_2167826738365259_closeModal_izi8n7g8g,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2167826738365259_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2167826738365259_clickPreventStop_gsbw9x5ki],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_ttnm5sm9n,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_4t76jdwtv,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_74esmhhtq,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_et9rwkx27,
    inst_evh_1040167445267876_submit_kb7iyffp6,
    submit: inst_evh_1040167445267876_submit_kb7iyffp6,
    inst_evh_1040167445267876_submit,
    inst_evh_2140198529698299_success_q73fn1pn3,
    inst_evh_2140198529698299_clickPreventStop_ld433incl,
    lock: inst_evh_2140198529698299_clickPreventStop_ld433incl,
    inst_evh_2140198529698299_clickPreventStop,
    inst_evh_2167826738365259_clickPreventStop_gsbw9x5ki,
    inst_evh_2167826738365259_clickPreventStop,
  };

  const evh_7315092382398562_close_ttnm5sm9n = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_ttnm5sm9n.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_4t76jdwtv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_4t76jdwtv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_74esmhhtq = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_74esmhhtq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_et9rwkx27 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_et9rwkx27.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_kb7iyffp6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_kb7iyffp6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_success_q73fn1pn3 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_success_q73fn1pn3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_ld433incl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_ld433incl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop_gsbw9x5ki = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop_gsbw9x5ki.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2167826738365259_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2167826738365259_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="RentalProperty"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="RentalProperty"
        :id="formId"
        class="container"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup name="property_unit" :label="gettext('Real Property')">
          <UcSiteSelector :item="item" path="property_unit"> </UcSiteSelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_from"
          :label="gettext('Valid from')"
          class="w-auto"
        >
          <ItpFormDatetime
            type="date"
            name="valid_from"
            v-model="item.valid_from"
            :readonly="item.locked"
            :defaultValue="$fn.today()"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="valid_to"
          :label="gettext('Valid to')"
          class="w-auto"
        >
          <ItpFormDatetime
            type="date"
            name="valid_to"
            v-model="item.valid_to"
            :readonly="item.locked"
          >
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup name="inactive">
          <ItpFormCheckbox
            name="inactive"
            :label="gettext('Inactive')"
            inline
            v-model="item.inactive"
            :readonly="item.locked"
          >
          </ItpFormCheckbox>
        </ItpFormGroup>
        <ItpFormGroup name="notes" :label="gettext('Notes')">
          <ItpFormTextarea
            name="notes"
            v-model="item.notes"
            :readonly="item.locked"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Lock')"
          variant="default"
          :spinning="actions.lock.isRunning"
          class="me-auto"
          v-if="resourceId && !item.locked"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          :form="formId"
          :text="gettext('Save')"
          type="submit"
          variant="primary"
          :spinning="actions.submit.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
          v-if="!item.locked"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2167826738365259_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
